/**
 * Public Listings API Service
 *
 * Fetches the public-facing listing DTO from `GET /api/v1/listings/public/:slug`.
 * The endpoint is unauthenticated; backend rejects with 404 on
 * unpublished/withdrawn slugs (no existence-leak between published-but-
 * unpublished vs never-existed).
 *
 * Wire shape mirrors the backend's `PublicListing` type — Date objects
 * come over as ISO strings, and `addressPrivate` is intentionally absent
 * (the backend never serializes it for non-owners; double-check via the
 * additive-leak test in listings.service.test.ts on the backend).
 */

import { apiClient } from "@/lib/api-client";

const API_BASE = "/api/v1";

export type PropertyType = "apartment" | "house" | "villa" | "plot";
export type TransactionType = "sale" | "rental" | "commercial";
export type ListingStatus =
	| "active"
	| "under_negotiation"
	| "sold"
	| "withdrawn";

export interface PublicListingRealtor {
	id: string;
	name: string;
	agencyName: string | null;
}

export interface PublicListing {
	id: string;
	realtor: PublicListingRealtor;
	propertyType: PropertyType;
	transactionType: TransactionType;
	bhk: number | null;
	sizeSqft: number | null;
	addressPublic: string;
	city: string;
	locality: string;
	askingPrice: number;
	headline: string;
	description: string | null;
	images: string[];
	amenities: string[];
	status: ListingStatus;
	isExclusive: boolean;
	publicSlug: string | null;
	createdAt: string;
	updatedAt: string;
}

interface BackendErrorBody {
	message?: string;
	error?: string;
}
interface AxiosLikeError {
	response?: { data?: BackendErrorBody; status?: number };
	code?: string;
	message?: string;
}

interface ApiResult<T> {
	success: boolean;
	error?: string;
	errorCode?: string | null;
	notFound?: boolean;
	data?: T;
}

export async function getPublicListing(
	slug: string,
): Promise<ApiResult<PublicListing>> {
	try {
		const response = await apiClient.get<{
			data: { listing: PublicListing };
		}>(`${API_BASE}/listings/public/${encodeURIComponent(slug)}`);
		return { success: true, data: response.data.data.listing };
	} catch (error: unknown) {
		const err = error as AxiosLikeError;
		// 404 is the expected miss — surface as `notFound` so the page can
		// render a dedicated "this listing isn't available" view rather
		// than a generic error toast.
		if (err.response?.status === 404) {
			return { success: false, notFound: true };
		}
		const body = err.response?.data;
		if (!err.response) {
			return {
				success: false,
				error:
					err.code === "ECONNABORTED" || err.message?.includes("timeout")
						? "Request timed out. Check your connection and try again."
						: "Couldn't reach the server. Check your connection and try again.",
				errorCode: "NETWORK_ERROR",
			};
		}
		return {
			success: false,
			error: body?.message ?? "Couldn't load this listing.",
			errorCode: body?.error ?? null,
		};
	}
}
